import { Component, computed, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { ModalComponent } from '../ui/modal/modal.component';
import { GlobalDropZoneService } from './global-drop-zone.service';
import { UploadTrackerService } from '../upload-tracker';
import { ExpensesService } from '../../features/expenses/data/expenses.service';

interface FilePreview {
  file: File;
  name: string;
  size: string;
  isImage: boolean;
  objectUrl: string | null;
}

@Component({
  selector: 'app-upload-confirm-modal',
  standalone: true,
  imports: [ModalComponent],
  templateUrl: './upload-confirm-modal.component.html',
  styleUrl: './upload-confirm-modal.component.scss',
})
export class UploadConfirmModalComponent implements OnDestroy {
  protected readonly dropZone = inject(GlobalDropZoneService);
  private readonly uploadTracker = inject(UploadTrackerService);
  private readonly expensesService = inject(ExpensesService);

  protected readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly objectUrls: string[] = [];

  protected readonly filePreviews = computed<FilePreview[]>(() => {
    const files = this.dropZone.droppedFiles();
    return files.map(file => {
      const isImage = /\.(jpe?g|png)$/i.test(file.name);
      let objectUrl: string | null = null;
      if (isImage) {
        objectUrl = URL.createObjectURL(file);
        this.objectUrls.push(objectUrl);
      }
      return {
        file,
        name: file.name,
        size: this.formatSize(file.size),
        isImage,
        objectUrl,
      };
    });
  });

  protected readonly isEmpty = computed(() => this.dropZone.droppedFiles().length === 0);

  onConfirm(): void {
    const files = this.dropZone.droppedFiles();
    if (files.length === 0) return;

    this.uploadTracker.startUpload({
      label: 'Gider Dosyaları',
      files: [...files],
      uploadFn: (file) => this.expensesService.quickUpload(file),
    });

    this.revokeUrls();
    this.dropZone.clearFiles();
    this.dropZone.showConfirmModal.set(false);
  }

  onClose(): void {
    this.revokeUrls();
    this.dropZone.clearFiles();
    this.dropZone.showConfirmModal.set(false);
  }

  removeFile(index: number): void {
    const preview = this.filePreviews()[index];
    if (preview?.objectUrl) {
      URL.revokeObjectURL(preview.objectUrl);
    }
    this.dropZone.removeFile(index);
  }

  triggerFileInput(): void {
    this.fileInput()?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length > 0) {
      this.dropZone.addFiles(files);
    }
    input.value = '';
  }

  ngOnDestroy(): void {
    this.revokeUrls();
  }

  private revokeUrls(): void {
    for (const url of this.objectUrls) {
      URL.revokeObjectURL(url);
    }
    this.objectUrls.length = 0;
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
